import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './lib/supabase';
import { 
  MessageCircle, 
  Send, 
  Phone, 
  Video, 
  MoreVertical, 
  Search,
  Smile,
  Paperclip,
  Mic,
  CheckCheck
} from 'lucide-react';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState('auth'); // auth, verification, chat
  
  // Chat state
  const [activeChat, setActiveChat] = useState(null);
  const [chats, setChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    checkUser();
  }, []);

  useEffect(() => {
    if (user) {
      fetchChats();
    }
  }, [user]);

  useEffect(() => {
    if (activeChat) {
      fetchMessages(activeChat.id);
    }
  }, [activeChat]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const checkUser = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user || null);
      if (session?.user) {
        setStep('chat');
      }
    } catch (error) {
      console.error('Error checking user:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handlePhoneAuth = async () => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        phone: phoneNumber,
      });
      
      if (error) throw error;
      setStep('verification');
    } catch (error) {
      alert('Error sending verification code: ' + error.message);
    }
  };

  const verifyCode = async () => {
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        phone: phoneNumber,
        token: verificationCode,
        type: 'sms'
      });
      
      if (error) throw error;
      setUser(data.user);
      setStep('chat');
    } catch (error) {
      alert('Error verifying code: ' + error.message);
    }
  };

  const fetchChats = async () => {
    try {
      // This would fetch actual chats from database
      // For now, using demo data
      setChats([
        {
          id: 1,
          name: "Prayer Group",
          lastMessage: "Let's pray for peace in our hearts",
          timestamp: "2:30 PM",
          avatar: "PG",
          unread: 2,
          isGroup: true
        },
        {
          id: 2,
          name: "Sarah Miller",
          lastMessage: "Blessed Sunday to you! 🙏",
          timestamp: "1:15 PM",
          avatar: "SM",
          unread: 0,
          isGroup: false
        },
        {
          id: 3,
          name: "Youth Ministry",
          lastMessage: "Meeting tonight at 7 PM",
          timestamp: "12:45 PM",
          avatar: "YM",
          unread: 5,
          isGroup: true
        }
      ]);
    } catch (error) {
      console.error('Error fetching chats:', error);
    }
  };

  const fetchMessages = (chatId) => {
    // Demo messages for selected chat
    const demoMessages = {
      1: [
        { id: 1, text: "Good morning everyone! 🌅", sender: "Sarah", timestamp: "9:00 AM", isSent: false },
        { id: 2, text: "Morning! How is everyone doing?", sender: "You", timestamp: "9:05 AM", isSent: true },
        { id: 3, text: "Blessed and grateful! Praying for a peaceful day", sender: "John", timestamp: "9:10 AM", isSent: false },
        { id: 4, text: "Amen to that! Let's pray for peace in our hearts", sender: "Mary", timestamp: "9:15 AM", isSent: false },
      ],
      2: [
        { id: 1, text: "Hi! How was church today?", sender: "Sarah Miller", timestamp: "1:00 PM", isSent: false },
        { id: 2, text: "It was wonderful! Pastor's message really touched my heart", sender: "You", timestamp: "1:05 PM", isSent: true },
        { id: 3, text: "Blessed Sunday to you! 🙏", sender: "Sarah Miller", timestamp: "1:15 PM", isSent: false },
      ],
      3: [
        { id: 1, text: "Don't forget about youth group tonight!", sender: "Pastor Mike", timestamp: "12:30 PM", isSent: false },
        { id: 2, text: "What time again?", sender: "Alex", timestamp: "12:35 PM", isSent: false },
        { id: 3, text: "Meeting tonight at 7 PM", sender: "Pastor Mike", timestamp: "12:45 PM", isSent: false },
        { id: 4, text: "I'll be there! 🙌", sender: "You", timestamp: "1:00 PM", isSent: true },
      ]
    };
    
    setMessages(demoMessages[chatId] || []);
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    
    const message = {
      id: Date.now(),
      text: newMessage,
      sender: "You",
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isSent: true
    };
    
    setMessages(prev => [...prev, message]);
    setNewMessage('');
    
    // Update chat preview
    setChats(prev => prev.map(chat => 
      chat.id === activeChat.id 
        ? { ...chat, lastMessage: newMessage, timestamp: message.timestamp }
        : chat
    ));
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setStep('auth');
      setActiveChat(null);
      setMessages([]);
      setChats([]);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-chat-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-faith-blue rounded-full flex items-center justify-center mb-4 mx-auto">
            <MessageCircle className="w-8 h-8 text-white" />
          </div>
          <div className="text-lg text-gray-600">Loading FaithChat...</div>
        </div>
      </div>
    );
  }

  if (step === 'auth') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-faith-blue to-faith-purple flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-faith-blue rounded-full flex items-center justify-center mb-4 mx-auto">
              <MessageCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800">FaithChat</h1>
            <p className="text-gray-600 mt-2">Connect with fellow believers</p>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+1 (555) 123-4567"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-faith-blue focus:border-transparent"
              />
            </div>
            
            <button
              onClick={handlePhoneAuth}
              className="w-full bg-faith-blue text-white py-3 rounded-lg font-medium hover:bg-blue-600 transition-colors"
            >
              Send Verification Code
            </button>
            
            <p className="text-xs text-gray-500 text-center">
              By continuing, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'verification') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-faith-blue to-faith-purple flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-faith-green rounded-full flex items-center justify-center mb-4 mx-auto">
              <Phone className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Verify Your Number</h1>
            <p className="text-gray-600 mt-2">Enter the code sent to {phoneNumber}</p>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification Code
              </label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="123456"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-faith-green focus:border-transparent text-center text-lg"
                maxLength={6}
              />
            </div>
            
            <button
              onClick={verifyCode}
              className="w-full bg-faith-green text-white py-3 rounded-lg font-medium hover:bg-green-600 transition-colors"
            >
              Verify & Continue
            </button>
            
            <button
              onClick={() => setStep('auth')}
              className="w-full text-faith-blue py-2 text-sm hover:underline"
            >
              Change phone number
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="bg-faith-blue text-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-6 h-6" />
              </div>
              <h1 className="text-xl font-semibold">FaithChat</h1>
            </div>
            <button
              onClick={signOut}
              className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors"
            >
              <MoreVertical className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search conversations"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-faith-blue focus:border-transparent"
            />
          </div>
        </div>

        {/* Chat List */}
        <div className="flex-1 overflow-y-auto">
          {chats.filter(chat => 
            chat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            chat.lastMessage.toLowerCase().includes(searchTerm.toLowerCase())
          ).map((chat) => (
            <div
              key={chat.id}
              onClick={() => setActiveChat(chat)}
              className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                activeChat?.id === chat.id ? 'bg-blue-50 border-faith-blue border-l-4' : ''
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-faith-blue rounded-full flex items-center justify-center">
                  <span className="text-white font-medium">{chat.avatar}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-900 truncate">
                      {chat.name}
                    </h3>
                    <span className="text-xs text-gray-500">{chat.timestamp}</span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-sm text-gray-600 truncate">
                      {chat.lastMessage}
                    </p>
                    {chat.unread > 0 && (
                      <span className="bg-faith-green text-white text-xs rounded-full px-2 py-1 min-w-5 text-center">
                        {chat.unread}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {activeChat ? (
          <>
            {/* Chat Header */}
            <div className="bg-white border-b border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-faith-blue rounded-full flex items-center justify-center">
                    <span className="text-white font-medium">{activeChat.avatar}</span>
                  </div>
                  <div>
                    <h2 className="font-semibold text-gray-900">{activeChat.name}</h2>
                    <p className="text-sm text-gray-500">
                      {activeChat.isGroup ? `${messages.length} messages` : 'last seen recently'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <Phone className="w-5 h-5 text-gray-600" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <Video className="w-5 h-5 text-gray-600" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <MoreVertical className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-chat-bg">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.isSent ? 'justify-end' : 'justify-start'}`}
                >
                  <div className="max-w-xs lg:max-w-md">
                    {!message.isSent && (
                      <p className="text-xs text-gray-500 mb-1 px-2">
                        {message.sender}
                      </p>
                    )}
                    <div
                      className={`px-4 py-2 rounded-2xl ${
                        message.isSent
                          ? 'bg-message-sent text-white'
                          : 'bg-white text-gray-900'
                      }`}
                    >
                      <p className="text-sm">{message.text}</p>
                      <div className={`flex items-center justify-end mt-1 space-x-1 ${
                        message.isSent ? 'text-blue-100' : 'text-gray-400'
                      }`}>
                        <span className="text-xs">{message.timestamp}</span>
                        {message.isSent && (
                          <CheckCheck className="w-3 h-3" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="bg-white border-t border-gray-200 p-4">
              <div className="flex items-center space-x-3">
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <Paperclip className="w-5 h-5 text-gray-600" />
                </button>
                <div className="flex-1 flex items-center bg-gray-100 rounded-full">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Type a message..."
                    className="flex-1 px-4 py-2 bg-transparent focus:outline-none"
                  />
                  <button className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                    <Smile className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
                {newMessage.trim() ? (
                  <button
                    onClick={sendMessage}
                    className="p-2 bg-faith-blue hover:bg-blue-600 rounded-full transition-colors"
                  >
                    <Send className="w-5 h-5 text-white" />
                  </button>
                ) : (
                  <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                    <Mic className="w-5 h-5 text-gray-600" />
                  </button>
                )}
              </div>
            </div>
          </>
        ) : (
          /* No Chat Selected */
          <div className="flex-1 flex items-center justify-center bg-chat-bg">
            <div className="text-center">
              <div className="w-32 h-32 bg-gray-200 rounded-full flex items-center justify-center mb-6 mx-auto">
                <MessageCircle className="w-16 h-16 text-gray-400" />
              </div>
              <h3 className="text-xl font-medium text-gray-800 mb-2">Welcome to FaithChat</h3>
              <p className="text-gray-600">Select a conversation to start chatting</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
